<script lang="ts">
    import { createEventDispatcher } from 'svelte';

	export let value: number = 0;
	export let integerOnly: boolean = false;
	export let steps: number[] = [1, 5, 10];
    export let min: number | null = null;
    export let max: number | null = null;
    export let inputWidth: number = 50;
    export let buttonWidth: number = 30;
	export let disabled: boolean = false;

    let dispatch = createEventDispatcher();
    let previousValue = value;

	$: sortedSteps = [...steps]
		.filter((n) => n > 0)
		.sort((a, b) => a - b);

	function sanitize(value: string): number {
		if (value.trim() === '' || value === '-') {
			return 0;
		}

		const parsed = integerOnly
			? parseInt(value, 10)
			: parseFloat(value);

		return Number.isFinite(parsed) ? parsed : 0;
	}

	function updateFromInput(event: Event) {
		const target = event.target as HTMLInputElement;
		value = sanitize(target.value);
        change(0);
	}

	function change(delta: number) {
		value += delta;

		if (integerOnly) {
			value = Math.round(value);
		}

        if (min !== null) {
            value = Math.max(value, min);
        }

        if (max !== null) {
            value = Math.min(value, max);
        }

        if (value !== previousValue) {
            previousValue = value;
            dispatch('change', { value });
        }
	}

	function getStep(level: number): number {
		if (sortedSteps.length === 0) return 1;
		return sortedSteps[Math.min(level, sortedSteps.length - 1)];
	}

	function handleKeydown(event: KeyboardEvent) {
		const smallest = getStep(0);
		const medium = getStep(1);
		const largest = getStep(sortedSteps.length - 1);

		switch (event.key) {
			case 'ArrowUp':
				event.preventDefault();
				change(smallest);
				break;

			case 'ArrowDown':
				event.preventDefault();
				change(-smallest);
				break;

			case 'PageUp':
				event.preventDefault();
				change(event.shiftKey ? largest : medium);
				break;

			case 'PageDown':
				event.preventDefault();
				change(event.shiftKey ? -largest : -medium);
				break;
		}
	}
</script>

<div class="number-stepper" class:disabled={disabled}>
	<div class="buttons decrement">
		{#each [...sortedSteps].reverse() as step, idx}
			<button
				type="button"
                style="min-width: {buttonWidth}px; z-index: {idx}"
				on:click={() => change(-step)}
			>
				-{step}
			</button>
		{/each}
	</div>

	<input
		type="text"
		inputmode={integerOnly ? 'numeric' : 'decimal'}
        style="width: {inputWidth}px; z-index: {steps.length}"
		bind:value={value}
		on:input={updateFromInput}
		on:keydown={handleKeydown}
	/>

	<div class="buttons increment">
		{#each sortedSteps as step, idx}
			<button
				type="button"
                style="min-width: {buttonWidth}px; z-index: {sortedSteps.length - idx - 1}"
				on:click={() => change(step)}
			>
				+{step}
			</button>
		{/each}
	</div>
</div>

<style>
	.number-stepper {
		display: flex;
		align-items: center;
	}

	.number-stepper.disabled {
		opacity: 0.5;
		pointer-events: none;
	}

	.buttons {
		display: flex;
        gap: -4px;
	}

    .buttons.decrement button {
        border-top-left-radius: 4px;
        border-bottom-left-radius: 4px;
        border: 1px solid #ccc;
        border-right: 0;
        margin-right: -4px;
        padding: 2px 8px 2px 4px
    }

    .buttons.increment button {
        border-top-right-radius: 4px;
        border-bottom-right-radius: 4px;
        border: 1px solid #999;
        border-left: 0;
        margin-left: -4px;
        padding: 2px 4px 2px 8px
    }

	input {
		width: 8rem;
		text-align: center;
        border: 1px solid #999;
        background-color: #fff;
	}

    button {
        cursor: pointer;
        background-color: #ccc;
    }
</style>